import { RefObject, useRef } from 'react';
import { StakesRankingChart_StakeFragment } from '@/graphql/generated';
import { NumberCircle } from '@/components/generic/NumberCircle';
import { StakeTag } from '../../../stake/StakeTag';
import { DownloadIcon, ExportIcon } from '@/components/icons';
import { ContextualMenu } from '@/components/generic/ContextualMenu';
import { useModal } from '@/components/modals/Modal';
import { ExportToImageModal } from '@/components/generic/export/ExportableToImage';
import {
  convertArrayToObject,
  getExportFileName,
} from '@/services/FileService';
import { utils, writeFileXLSX } from 'xlsx';
import { useTranslation } from '@/hooks/useTranslation';

export type RankedStakes = {
  stake: StakesRankingChart_StakeFragment;
  score: number | null;
};

export function StakesRankingChart({
  rankedStakes,
  title,
}: {
  rankedStakes: RankedStakes[];
  title: string;
}) {
  const exportableRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative">
      <div className="absolute top-4 right-4 z-40">
        <ExportMenu topStakes={rankedStakes} exportableRef={exportableRef} />
      </div>
      <div ref={exportableRef}>
        <div className="font-bold mb-4 pr-8">{title}</div>
        <div className="space-y-2 mb-4">
          {rankedStakes.map((rankedStake, index) => (
            <div key={rankedStake.stake.id} className="flex items-center gap-4">
              <div className="flex items-center space-x-2 grow">
                <NumberCircle number={index + 1} size={8} />
                <div>
                  <StakeTag
                    stake={rankedStake.stake}
                    disableDetailModalOpening={true}
                  />
                </div>
              </div>
              <div className="text-gray-500 text-xs shrink-0">
                score {(rankedStake.score || 0).toFixed(1)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExportMenu({
  topStakes,
  exportableRef,
}: {
  topStakes: RankedStakes[];
  exportableRef: RefObject<HTMLDivElement | null>;
}) {
  const button = <ExportIcon className="shrink-0 h-3.5 w-3.5 text-gray-500" />;
  return (
    <ContextualMenu button={button}>
      <ExportToXlsButton topStakes={topStakes} />
      <ExportToImageButton refToExport={exportableRef} />
    </ContextualMenu>
  );
}

function ExportToXlsButton({ topStakes }: { topStakes: RankedStakes[] }) {
  const { t, translateProperty } = useTranslation();
  const exportToXls = () => {
    const header = [
      t('survey.results.stakes_ranking.export.header.identifier'),
      t('survey.results.stakes_ranking.export.header.issue'),
      t('survey.results.stakes_ranking.export.header.score'),
      t('survey.results.stakes_ranking.export.header.ranking'),
    ];
    const rows = topStakes.map((rankedStake, index) => {
      const stakeName = translateProperty(rankedStake.stake, 'name');
      return [
        rankedStake.stake.id,
        stakeName,
        rankedStake.score || 0,
        index + 1,
      ];
    });

    // Convert array to object to be able to use xlsx library
    const asAnObject = convertArrayToObject(rows, header);

    const worksheet = utils.json_to_sheet(asAnObject, {
      skipHeader: false,
    });

    const workbook = utils.book_new();
    utils.book_append_sheet(
      workbook,
      worksheet,
      t('survey.results.stakes_ranking.export.header.ranking'),
    );
    writeFileXLSX(
      workbook,
      `${t('survey.results.stakes_ranking.export.filename')}.xlsx`,
      {
        compression: true,
      },
    );
  };

  return (
    <button className="contextual-menu-item" onClick={exportToXls}>
      <DownloadIcon />
      <div>{t('survey.results.stakes_ranking.export.xls_button')}</div>
    </button>
  );
}

function ExportToImageButton({
  refToExport,
}: {
  refToExport: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const modal = useModal();
  return (
    <button
      className="contextual-menu-item"
      onClick={() =>
        modal.openModal(
          <ExportToImageModal
            refToExport={refToExport}
            name={getExportFileName(
              t('survey.results.stakes_ranking.export.filename'),
            )}
          />,
          {
            title: t('survey.results.stakes_ranking.export.title'),
            widthClass: 'w-8/12',
          },
        )
      }
    >
      <DownloadIcon />
      <div>{t('survey.results.stakes_ranking.export.image_button')}</div>
    </button>
  );
}
